/* ============================================================================
 * sample-data.js - deterministic sample dataset + adapter
 * ----------------------------------------------------------------------------
 * One generator, two consumers:
 *   - web/dev/fixture-server.js serves it over HTTP for local development
 *   - `sampleDataService` implements the dataService contract directly in the
 *     browser, so the static site can run with no backend (VITE_DATA_MODE=sample)
 * Shapes match meridian-server.js exactly. Seeded PRNG → stable across loads.
 * ==========================================================================*/

import { countyName } from "./tx-counties.js";

/* --------------------------------------------------- deterministic PRNG */
function mulberry32(seed) {
  let a = seed >>> 0;
  return () => {
    a |= 0; a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}
const hash = (s) => [...String(s)].reduce((h, c) => (Math.imul(h, 31) + c.charCodeAt(0)) | 0, 7);

/* ------------------------------------------------------------ sample world */
const CITIES = [
  ["AUSTIN", "227", 1.35], ["HOUSTON", "101", 1.5], ["DALLAS", "57", 1.4],
  ["SAN ANTONIO", "15", 1.2], ["FORT WORTH", "220", 1.0], ["EL PASO", "71", 0.7],
  ["WACO", "161", 0.5], ["LUBBOCK", "152", 0.45], ["GALVESTON", "84", 0.5],
  ["NEW BRAUNFELS", "46", 0.4],
];
const NAMES = [
  ["THE GILDED ROOSTER", "Bar"], ["LONGHORN SOCIAL HALL", "Live Music"], ["CANTINA DEL SOL", "Restaurant"],
  ["MIDNIGHT PECAN LOUNGE", "Lounge"], ["BLUEBONNET BREWING CO", "Brewery"], ["HOTEL SETTLES BAR", "Hotel"],
  ["SALT & SMOKE KITCHEN", "Restaurant"], ["NEON ARMADILLO NIGHTCLUB", "Nightclub"], ["THE MERIDIAN CLUB", "Bar"],
  ["CAST IRON GRILL", "Restaurant"], ["VINEYARD ROW WINE BAR", "Wine Bar"], ["LA COCINA DE PILAR", "Restaurant"],
  ["WHISKEY LATITUDE", "Bar"], ["THE BRASS COMPASS", "Bar"], ["PRAIRIE FIRE STEAKHOUSE", "Restaurant"],
  ["HONKY TONK PALACE", "Live Music"], ["GULF & MAIN OYSTER HOUSE", "Restaurant"], ["CEDAR & SAGE ROOFTOP", "Lounge"],
  ["EL JARDIN TAQUERIA", "Restaurant"], ["IRONWORKS TAP ROOM", "Bar"], ["THE VELVET LONGNECK", "Nightclub"],
  ["BAYOU BISTRO", "Restaurant"], ["SILVER SPUR SALOON", "Live Music"], ["THE STANDARD KITCHEN", "Restaurant"],
  ["PARLOR OF THE REPUBLIC", "Bar"], ["CORAZON COCINA", "Restaurant"], ["HILL COUNTRY SOCIAL", "Bar"],
  ["DRIFTWOOD GRILL", "Restaurant"], ["THE LAMPLIGHT LOUNGE", "Lounge"], ["MOONTOWER MUSIC HALL", "Live Music"],
];
const STREETS = ["CONGRESS AVE", "MAIN ST", "COMMERCE ST", "SOUTH LAMAR BLVD", "WESTHEIMER RD", "MCKINNEY AVE", "AUSTIN AVE", "STRAND ST", "BROADWAY", "6TH ST"];

/* 24 months ending 2026-05 (dataset lags ~1-2 months). */
export const MONTHS = [];
{
  let y = 2024, m = 6;
  for (let i = 0; i < 24; i++) {
    MONTHS.push(`${y}-${String(m).padStart(2, "0")}`);
    m++; if (m > 12) { m = 1; y++; }
  }
}
const monthIndex = (ym) => MONTHS.indexOf(ym);

/* Build the permit universe: ~9 businesses per city. */
const PERMITS = [];
CITIES.forEach(([city, county, weight], ci) => {
  for (let k = 0; k < 9; k++) {
    const ni = (ci * 9 + k) % NAMES.length;
    const [name, cat] = NAMES[ni];
    const permit = `MB${String(700000 + ci * 1000 + k * 37)}`;
    PERMITS.push({
      permit, name: k >= NAMES.length / 3 && ci > 4 ? `${name} ${city.split(" ")[0]}` : name,
      cat, city, county,
      addr: `${100 + ((ci * 97 + k * 53) % 8800)} ${STREETS[(ci + k) % STREETS.length]}`,
      zip: String(73301 + ci * 113 + k),
      base: (140000 + mulberry32(hash(permit))() * 720000) * weight,
    });
  }
});

/* Monthly receipts for a permit: base × seasonality × trend × noise. */
function receipts(p, ym) {
  const i = monthIndex(ym);
  if (i < 0) return null;
  const rnd = mulberry32(hash(p.permit + ym));
  const mm = Number(ym.slice(5));
  const season = 1 + 0.16 * Math.sin(((mm - 3) / 12) * Math.PI * 2) + (mm === 12 ? 0.1 : 0) + (mm === 3 ? 0.08 : 0);
  const trend = 1 + i * 0.006 * (0.5 + mulberry32(hash(p.permit))());
  const noise = 0.86 + rnd() * 0.28;
  const total = Math.round(p.base * season * trend * noise);
  const lf = { Bar: 0.62, Lounge: 0.6, Nightclub: 0.55, "Live Music": 0.5, Restaurant: 0.42, Hotel: 0.5, Brewery: 0.18, "Wine Bar": 0.2 }[p.cat] ?? 0.5;
  const wf = p.cat === "Wine Bar" ? 0.55 : p.cat === "Restaurant" ? 0.22 : 0.08 + rnd() * 0.05;
  const cf = p.cat === "Nightclub" || p.cat === "Live Music" ? 0.06 + rnd() * 0.04 : 0;
  const liquor = Math.round(total * lf), wine = Math.round(total * wf), cover = Math.round(total * cf);
  const beer = Math.max(0, total - liquor - wine - cover);
  return { liquor, wine, beer, cover, total };
}

const pctChange = (cur, prev) => (prev > 0 ? +(((cur - prev) / prev) * 100).toFixed(1) : 0);
const prevKey = (ym) => MONTHS[monthIndex(ym) - 1];

function shape(p, ym) {
  const r = receipts(p, ym);
  if (!r || r.total <= 0) return null;
  const prevYm = prevKey(ym);
  const pr = prevYm ? receipts(p, prevYm) : null;
  const mix = { l: +(r.liquor / r.total).toFixed(4), w: +(r.wine / r.total).toFixed(4), b: +(r.beer / r.total).toFixed(4), c: +(r.cover / r.total).toFixed(4) };
  return {
    id: p.permit, permit: p.permit, name: p.name, addr: p.addr, zip: p.zip,
    city: p.city, county: countyName(p.county), cat: p.cat,
    total: r.total, chg: pr ? pctChange(r.total, pr.total) : 0, mix,
  };
}

/* ---------------------------------------------------------------- payloads */
const EMPTY_SNAPSHOT = (month) => ({
  period: month, prior: null,
  metrics: { trackedLocations: 0, trackedDelta: 0, reportedSales: 0, salesDelta: 0, avgLocation: 0, avgDelta: 0, highestGrossing: null },
  businesses: [], pulse: [],
});

export function snapshot(ym, city = "All cities", limit = 500, county = null) {
  if (monthIndex(ym) < 0) return EMPTY_SNAPSHOT(ym);
  const inCity = (p) => city === "All cities" || p.city.toUpperCase() === city.toUpperCase();
  const inCounty = (p) => county == null || String(p.county) === String(county);
  const scoped = (city && city !== "All cities") || county != null;
  const scopePermits = PERMITS.filter((p) => inCity(p) && inCounty(p));
  const businesses = scopePermits.map((p) => shape(p, ym)).filter(Boolean).sort((a, b) => b.total - a.total).slice(0, limit);
  const prevYm = prevKey(ym);
  const sum = (rs) => rs.reduce((s, r) => s + r.total, 0);

  /* Totals are computed over the in-scope permit universe, not the capped
   * top-N businesses, so City/County KPIs reflect the whole city/county. */
  const totalPermits = scoped ? scopePermits : PERMITS;
  const now = totalPermits.map((p) => receipts(p, ym)).filter(Boolean);
  const prev = prevYm ? totalPermits.map((p) => receipts(p, prevYm)).filter(Boolean) : [];
  const totals = { locations: now.length, sales: sum(now) };
  const totalsPrev = { locations: prev.length, sales: sum(prev) };

  const cityAgg = (key) => {
    const m = new Map();
    PERMITS.forEach((p) => {
      const r = receipts(p, key); if (!r) return;
      m.set(p.city, (m.get(p.city) || 0) + r.total);
    });
    return m;
  };
  const nowCities = cityAgg(ym), prevCities = prevYm ? cityAgg(prevYm) : new Map();
  const pulse = [...nowCities.entries()].sort((a, b) => b[1] - a[1]).slice(0, 6)
    .map(([c, t]) => ({ city: c, total: t, chg: pctChange(t, prevCities.get(c) || 0) }));

  /* Real trailing-12-month total-sales series for the in-scope permits. */
  const trend = [];
  for (let i = 11; i >= 0; i--) {
    const idx = monthIndex(ym) - i;
    const key = MONTHS[idx];
    if (!key) { trend.push({ m: `t-${i}`, v: 0 }); continue; }
    trend.push({ m: key, v: sum(totalPermits.map((p) => receipts(p, key)).filter(Boolean)) });
  }

  const top = businesses[0];
  return {
    period: ym, prior: prevYm || null,
    metrics: {
      trackedLocations: totals.locations,
      trackedDelta: pctChange(totals.locations, totalsPrev.locations),
      reportedSales: totals.sales,
      salesDelta: pctChange(totals.sales, totalsPrev.sales),
      avgLocation: totals.locations ? Math.round(totals.sales / totals.locations) : 0,
      avgDelta: pctChange(totals.locations ? totals.sales / totals.locations : 0, totalsPrev.locations ? totalsPrev.sales / totalsPrev.locations : 0),
      highestGrossing: top ? { name: top.name, city: top.city, county: top.county, total: top.total, chg: top.chg, permit: top.permit } : null,
    },
    businesses, pulse, trend,
  };
}

export function business(permit) {
  const p = PERMITS.find((x) => x.permit === permit);
  if (!p) return null;
  const series = MONTHS.map((ym) => ({ ym, ...receipts(p, ym) }));
  const latest = series[series.length - 1];
  const trend = series.slice(-12).map((s) => ({ m: s.ym, v: s.total }));
  const mix = { l: +(latest.liquor / latest.total).toFixed(4), w: +(latest.wine / latest.total).toFixed(4), b: +(latest.beer / latest.total).toFixed(4), c: +(latest.cover / latest.total).toFixed(4) };
  const prevYearTotal = series[series.length - 13]?.total ?? null;
  const yoy = prevYearTotal ? pctChange(latest.total, prevYearTotal) : 0;
  /* Full history, newest first - UI shows 12 months on Free, all on Pro. */
  const historical = series.slice().reverse().map((s) => {
    const yrAgo = series[monthIndex(s.ym) - 12];
    return { month: s.ym, liquor: s.liquor, wine: s.wine, beer: s.beer, cover: s.cover, total: s.total, yoy: yrAgo ? pctChange(s.total, yrAgo.total) : 0 };
  });
  const monthMoM = pctChange(latest.total, series[series.length - 2].total);
  return {
    business: {
      id: permit, permit, name: p.name, addr: p.addr, zip: p.zip,
      city: p.city, county: countyName(p.county), cat: p.cat,
      total: latest.total, chg: monthMoM, period: latest.ym,
    },
    trend, historical, yoy, mix,
  };
}

export function search(q, ym, limit = 25) {
  const needle = q.toUpperCase();
  return PERMITS
    .filter((p) => p.name.includes(needle) || p.city.includes(needle) || p.permit.includes(needle) || p.zip.includes(needle))
    .map((p) => ({ p, r: receipts(p, ym) }))
    .filter((x) => x.r)
    .sort((a, b) => b.r.total - a.r.total)
    .slice(0, limit)
    .map(({ p, r }) => ({ id: p.permit, permit: p.permit, name: p.name, city: p.city, county: countyName(p.county), total: r.total, cat: p.cat }));
}

export const PERMIT_COUNT = PERMITS.length;

/* ------------------------------------------- browser dataService adapter */
export const sampleDataService = {
  async months() { return { months: MONTHS, latest: MONTHS[MONTHS.length - 1] }; },
  async cityList() { return [...new Set(PERMITS.map((p) => p.city))].sort(); },
  async countyTotals(month) {
    const by = new Map();
    PERMITS.forEach((p) => {
      const r = receipts(p, month); if (!r) return;
      const g = by.get(p.county) || { code: p.county, total: 0, venues: 0 };
      g.total += r.total; g.venues++; by.set(p.county, g);
    });
    return [...by.values()].sort((a, b) => b.total - a.total);
  },
  async cityStats(month, limit = 60) {
    const by = new Map();
    PERMITS.forEach((p) => {
      const r = receipts(p, month); if (!r) return;
      const g = by.get(p.city) || { city: p.city, county: countyName(p.county), total: 0, venues: 0 };
      g.total += r.total; g.venues++; by.set(p.city, g);
    });
    return [...by.values()].sort((a, b) => b.total - a.total).slice(0, limit);
  },
  async snapshot(month, { city = "All cities", county = null, limit = 500 } = {}) { return snapshot(month, city, limit, county); },
  async business(permit) {
    const d = business(permit);
    if (!d) throw new Error("Texas Bar Sales API 404 · permit_not_found");
    return d;
  },
  async search(q, month, limit = 25) { return { results: q?.trim() ? search(q.trim(), month, limit) : [] }; },
};
